import { c as _c } from "react/compiler-runtime";
import { useState, type FC } from 'react';
import classNames from 'classnames';
import Image from 'next/image';
import { LiraAdvContainer } from '@sbt-web/adv';
import { Icon, IconSize } from '@sbt-web/ui';
import { type Shops, TrustProfileInfo, TrustProfilePresence } from '@sbt-web/network/types';
import { createOptimizelyInstance, OptimizelySubitoProvider } from '@sbt-web/houston-wrapper';
import { UserRating } from '@/client/components/Detail/UserRating';
import useUser from '@/client/contexts/UserContext';
import { PrivateUserAdsPublishedCounter } from '@/shared/components/PrivateUser/AdsPublishedCounter';
import { PrivateUserAdsOnlineCounter } from '@/shared/components/PrivateUser/AdsOnlineCounter';
import { PrivateUserCompletedDealsCounter } from '@/shared/components/PrivateUser/CompletedDealsCounter';
import { LiraContainerIds } from '@/shared/adv/lira/liraContainerIds';
import { AchievementBadges } from '@/shared/components/AchievementBadges';
import { reviewCountClickAction } from '@/tools/tracking/ProfileTracking';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import type { User, UserFeedbackResponse } from '@/types';
import { isProfileOwner } from '@/utils/profile';
import { GoToShop } from '../GoToShop';
import { TextualReviewBox } from '../TextualReviewBox';
import { UserBadge } from '../UserBadge';
import styles from './index.module.scss';
interface Props {
  user: User;
  ownerId: string;
  trustInfo?: TrustProfileInfo;
  feedbacks?: UserFeedbackResponse;
  shop?: Shops;
}
interface PresenceProps {
  isOwner: boolean;
  presence: TrustProfilePresence | undefined;
}
const optimizely = createOptimizelyInstance(process.env.NEXT_PUBLIC_INTERNAL_ENVIRONMENT);

/**
 * after the ad detail page app router migration, move this component
 * in /app/components folder and manage the styles. So it can be used in the
 * PrivateUserProfileBadge component
 */
const Presence = t0 => {
  const $ = _c(3);
  const {
    isOwner,
    presence
  } = t0;
  if (isOwner && presence?.enabled) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className={styles["user-online-status"]}><span className={classNames(styles["presence-indicator"], styles.online)} /><p className="body-text small">Online</p></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== presence) {
    t1 = presence?.enabled && presence?.presenceText ? <div className={styles["user-online-status"]}><span className={classNames(styles["presence-indicator"], {
        [styles.online]: presence.status === "ONLINE"
      })} /><p className="body-text small">{presence.presenceText}</p></div> : null;
    $[1] = presence;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const ReplyTime = t0 => {
  const $ = _c(3);
  const {
    replyTimeText
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Image src="/static/img/reply-time/reply.svg" alt="" height="16" width="16" quality={100} decoding="async" className={styles["reply-icon"]} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== replyTimeText) {
    t2 = <div className={styles["user-online-status"]}>{t1}<p className="body-text small">{replyTimeText}</p></div>;
    $[1] = replyTimeText;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const UserData: FC<Props> = t0 => {
  const $ = _c(73);
  const {
    user,
    ownerId,
    trustInfo,
    feedbacks,
    shop
  } = t0;
  const {
    user: publicUser
  } = useUser();
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const t1 = publicUser?.id;
  let t2;
  if ($[0] !== ownerId || $[1] !== t1) {
    t2 = isProfileOwner(ownerId, t1);
    $[0] = ownerId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isOwner = t2;
  const reviewCount = trustInfo?.reputation.feedback?.sourceCounts?.MEMBER;
  const reviewScore = trustInfo?.reputation.feedback?.sourceScores?.MEMBER;
  const dealsCount = trustInfo?.reputation.feedback?.receivedCount ?? 0;
  const shopUrl = shop?.shops[0]?.urls?.default;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = classNames(styles["user-stats__button"], styles["user-stats__button--hover"]);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const userStateButtonClassName = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = classNames(styles["user-stats__label"], "caption", "small", "book");
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const userStateLabelClassName = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = classNames(styles["user-stats__counter"], "body-text", "semibold");
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const userStatCounterClassName = t5;
  let t6;
  if ($[6] !== user.name || $[7] !== user.picture) {
    t6 = <UserBadge picture={user.picture} name={user.name} />;
    $[6] = user.name;
    $[7] = user.picture;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = classNames("headline-5", styles.user_info__username);
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== user.name) {
    t8 = <h1 className={t7}>{user.name}</h1>;
    $[10] = user.name;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = getOrCreatePulse();
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== ownerId) {
    t10 = () => {
      setReviewModalOpen(true);
      reviewCountClickAction(ownerId);
    };
    $[13] = ownerId;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== ownerId || $[16] !== reviewCount || $[17] !== reviewScore || $[18] !== t10) {
    t11 = <UserRating userId={ownerId} pulseInstance={t9} score={reviewScore} reviewCount={reviewCount} showReviewCount={false} onReviewCountClick={t10} />;
    $[15] = ownerId;
    $[16] = reviewCount;
    $[17] = reviewScore;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t8) {
    t12 = <div className={styles["user_info--vertical-box"]}>{t8}{t11}</div>;
    $[20] = t11;
    $[21] = t8;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t6) {
    t13 = <section className={styles.user_info}>{t6}{t12}</section>;
    $[23] = t12;
    $[24] = t6;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Icon name="Calendar" iconSize={IconSize.SM} />;
    t15 = classNames("body-text small");
    $[26] = t14;
    $[27] = t15;
  } else {
    t14 = $[26];
    t15 = $[27];
  }
  let t16;
  if ($[28] !== user.publishFromDate) {
    t16 = <div className={styles.trust_info__active_since}>{t14}<p className={t15}>{user.publishFromDate}</p></div>;
    $[28] = user.publishFromDate;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== trustInfo) {
    t17 = trustInfo?.communication.replyTimeText != undefined ? <ReplyTime replyTimeText={trustInfo.communication.replyTimeText} /> : null;
    $[30] = trustInfo;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const t18 = trustInfo?.presence;
  let t19;
  if ($[32] !== isOwner || $[33] !== t18) {
    t19 = <Presence presence={t18} isOwner={isOwner} />;
    $[32] = isOwner;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t16 || $[36] !== t17 || $[37] !== t19) {
    t20 = <div className={styles.trust_info}>{t16}{t17}{t19}</div>;
    $[35] = t16;
    $[36] = t17;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  const t21 = user?.badges;
  let t22;
  if ($[39] !== t21) {
    t22 = <AchievementBadges data={t21} />;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  const t23 = user?.publishedAds;
  let t24;
  if ($[41] !== t23) {
    t24 = <PrivateUserAdsPublishedCounter numberOfAdsPublished={t23} buttonClassName={styles["user-stats__button"]} labelClassName={userStateLabelClassName} counterClassName={userStatCounterClassName} />;
    $[41] = t23;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  const t25 = user?.onlineAds ?? null;
  let t26;
  if ($[43] !== t25) {
    t26 = <PrivateUserAdsOnlineCounter numberOfAdsOnline={t25} buttonClassName={styles["user-stats__button"]} labelClassName={userStateLabelClassName} counterClassName={userStatCounterClassName} />;
    $[43] = t25;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== dealsCount) {
    t27 = <PrivateUserCompletedDealsCounter dealsDone={dealsCount} buttonClassName={userStateButtonClassName} labelClassName={userStateLabelClassName} counterClassName={userStatCounterClassName} />;
    $[45] = dealsCount;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] !== t24 || $[48] !== t26 || $[49] !== t27) {
    t28 = <div className={styles["user-stats"]}>{t24}{t26}{t27}</div>;
    $[47] = t24;
    $[48] = t26;
    $[49] = t27;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  let t29;
  if ($[51] !== shopUrl) {
    t29 = shopUrl && <GoToShop url={shopUrl} />;
    $[51] = shopUrl;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = getOrCreatePulse()?.getEnvironmentId();
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  const t31 = feedbacks?.result;
  let t32;
  let t33;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => {
      setReviewModalOpen(true);
    };
    t33 = () => {
      setReviewModalOpen(false);
    };
    $[54] = t32;
    $[55] = t33;
  } else {
    t32 = $[54];
    t33 = $[55];
  }
  let t34;
  if ($[56] !== ownerId || $[57] !== reviewCount || $[58] !== reviewModalOpen || $[59] !== reviewScore || $[60] !== t31 || $[61] !== user.name) {
    t34 = <OptimizelySubitoProvider optimizely={optimizely} environmentId={t30}><TextualReviewBox ownerId={ownerId} userName={user.name} score={reviewScore} reviewCount={reviewCount} feedbacks={t31} modalOpen={reviewModalOpen} onPreviewClick={t32} onModalClose={t33} /></OptimizelySubitoProvider>;
    $[56] = ownerId;
    $[57] = reviewCount;
    $[58] = reviewModalOpen;
    $[59] = reviewScore;
    $[60] = t31;
    $[61] = user.name;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] !== t13 || $[64] !== t20 || $[65] !== t22 || $[66] !== t28 || $[67] !== t29 || $[68] !== t34) {
    t35 = <div className={styles.user_data_wrapper}>{t13}{t20}{t22}{t28}{t29}{t34}</div>;
    $[63] = t13;
    $[64] = t20;
    $[65] = t22;
    $[66] = t28;
    $[67] = t29;
    $[68] = t34;
    $[69] = t35;
  } else {
    t35 = $[69];
  }
  let t36;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <LiraAdvContainer idForAd={LiraContainerIds.belowTheRating} className={styles["adv-slot"]} />;
    $[70] = t36;
  } else {
    t36 = $[70];
  }
  let t37;
  if ($[71] !== t35) {
    t37 = <section className={styles.user_data_section}>{t35}{t36}</section>;
    $[71] = t35;
    $[72] = t37;
  } else {
    t37 = $[72];
  }
  return t37;
};
export default UserData;